import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { FIELD_PLACEHOLDER_COLOR, fieldLabelSx, buildAdornmentTextFieldSx } from '../common/formFieldStyles';
import { getLocalizedValue } from '../../constants/Languages';
import { formatCurrency } from '../../utils/currencyUtils';
import { estimateMargin, estimateRecipeCost } from '../../utils/serviceRecipeCostUtils';
import { stockUnitSuffix } from '../../utils/stockQuantityUtils';
import { BRAND } from '../../theme/brandTokens';

const MAX_RECIPE_LINES = 30;

const ServiceProductsUsedField = ({
  products = [],
  lines = [],
  onChange,
  servicePrice,
  currency,
  canSeeCosts = false,
}) => {
  const { t, i18n } = useTranslation();
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('');
  const estimate = useMemo(
    () => estimateRecipeCost(lines, canSeeCosts ? products : products.map((product) => ({ ...product, lastUnitCost: null }))),
    [lines, products, canSeeCosts],
  );
  const margin = canSeeCosts ? estimateMargin(servicePrice, estimate.totalCost) : null;
  const selectedProduct = products.find((item) => String(item.id) === String(productId));
  const quantityUnit = selectedProduct
    ? (stockUnitSuffix(selectedProduct.stockUnit) || t(`productsManagement.units.${selectedProduct.stockUnit}`))
    : '';

  const handleAdd = () => {
    const amount = Number(quantity);
    if (!productId || !Number.isFinite(amount) || amount <= 0 || lines.length >= MAX_RECIPE_LINES) {
      return;
    }

    if (lines.some((line) => String(line.productId) === String(productId))) {
      onChange(lines.map((line) => (
        String(line.productId) === String(productId) ? { ...line, quantity: amount } : line
      )));
    } else {
      onChange([...lines, { productId, quantity: amount }]);
    }

    setProductId('');
    setQuantity('');
  };

  return (
    <Box id="service-products-used" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t('stock.recipe.title')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(148px, 180px) auto' },
          gap: 1.5,
          alignItems: 'end',
        }}
      >
        <SalovareSelectField
          id="service-recipe-product"
          label={t('stock.recipe.product')}
          value={productId}
          onChange={(event) => setProductId(event.target.value)}
          displayEmpty
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                  {t('stock.recipe.product')}
                </Typography>
              );
            }
            const product = products.find((item) => String(item.id) === String(value));
            return getLocalizedValue(product?.name, i18n.language, undefined, '');
          }}
        >
          <MenuItem value="">
            <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{t('stock.recipe.product')}</Typography>
          </MenuItem>
          {products.map((product) => (
            <MenuItem key={product.id} value={product.id}>
              {getLocalizedValue(product.name, i18n.language, undefined, '')}
            </MenuItem>
          ))}
        </SalovareSelectField>

        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('stock.recipe.quantity')}
          </Typography>
          <TextField
            id="service-recipe-quantity"
            type="number"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            inputProps={{
              min: 0,
              step: '0.001',
              'aria-label': t('stock.recipe.quantity'),
            }}
            InputProps={quantityUnit ? {
              endAdornment: <InputAdornment position="end">{quantityUnit}</InputAdornment>,
            } : undefined}
            sx={buildAdornmentTextFieldSx()}
            fullWidth
          />
        </Box>

        <Button
          id="service-recipe-add"
          type="button"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={lines.length >= MAX_RECIPE_LINES}
          sx={{ textTransform: 'none', minHeight: 48, borderColor: BRAND.line, color: BRAND.accentText }}
        >
          {t('stock.recipe.add')}
        </Button>
      </Box>

      {lines.map((line) => {
        const product = products.find((item) => String(item.id) === String(line.productId));
        const detail = estimate.lines.find((item) => String(item.productId) === String(line.productId));
        const suffix = stockUnitSuffix(product?.stockUnit || line.stockUnit);
        return (
          <Box key={line.productId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getLocalizedValue(product?.name, i18n.language, undefined, line.name || line.productId)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {`${line.quantity}${suffix ? ` ${suffix}` : ''}`}
                {canSeeCosts && detail?.lineCost != null
                  ? ` · ${formatCurrency(detail.lineCost, currency)}`
                  : ''}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={t('stock.recipe.remove')}
              onClick={() => onChange(lines.filter((item) => item.productId !== line.productId))}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      })}

      {canSeeCosts ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {estimate.missingCost
              ? t('stock.recipe.costMissing')
              : t('stock.recipe.totalCost', { cost: formatCurrency(estimate.totalCost || 0, currency) })}
          </Typography>
          {margin != null ? (
            <Typography variant="body2" color="text.secondary">
              {t('stock.recipe.margin', { amount: formatCurrency(margin, currency) })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};

export default ServiceProductsUsedField;
